import { AccessType } from "@mirage/shared/compose/assist-api";
import type {
  AssistCustomTemplate,
  AssistTemplate,
  DefaultTemplate,
} from "@mirage/shared/compose/assist-template";
import { createDefaultTemplates } from "@mirage/shared/compose/assist-template";
import { useMemo } from "react";

interface UseTemplateCategoriesProps {
  templates: AssistCustomTemplate[];
}

interface UseTemplateCategoriesReturn {
  individualTemplates: AssistCustomTemplate[];
  teamTemplates: AssistCustomTemplate[];
  defaultTemplates: AssistTemplate[];
  sortedDefaultTemplates: AssistTemplate[];
  allTemplates: (AssistCustomTemplate | DefaultTemplate)[];
}

export function useTemplateCategories({
  templates,
}: UseTemplateCategoriesProps): UseTemplateCategoriesReturn {
  const defaultTemplates = useMemo(() => createDefaultTemplates(), []);

  const individualTemplates = useMemo(() => {
    return templates
      .filter(
        (template) =>
          "accessType" in template &&
          template.accessType === AccessType.INDIVIDUAL,
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [templates]);

  const teamTemplates = useMemo(() => {
    return templates
      .filter(
        (template) =>
          "accessType" in template && template.accessType === AccessType.TEAM,
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [templates]);

  const sortedDefaultTemplates = useMemo(() => {
    return [...defaultTemplates].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
  }, [defaultTemplates]);

  // Order: Individual -> Team -> Default (all sorted alphabetically within their category)
  const allTemplates = useMemo(() => {
    return [
      ...individualTemplates,
      ...teamTemplates,
      ...sortedDefaultTemplates,
    ];
  }, [individualTemplates, teamTemplates, sortedDefaultTemplates]);

  return {
    individualTemplates,
    teamTemplates,
    defaultTemplates,
    sortedDefaultTemplates,
    allTemplates,
  };
}
